/**
 * Shared dashboard types — imported by BOTH the renderer and the electron
 * main process (repo type-sharing rule). Keep this file dependency-light:
 * only `import type` from other pure type modules.
 *
 * Contract source: Docs/superpowers/plans/2026-07-10-sql-dashboards-plan.md (S1).
 * Later slices consume these types verbatim — do not rename fields casually.
 */
import type { ChartConfig, AggregationFunction } from '../../charts/types/chartTypes';

export type DashboardTileType = 'chart' | 'stat' | 'table' | 'pivot' | 'map' | 'note';

/**
 * Engine identity recorded on query tiles/params so S14 import-remap can offer
 * engine-compatible connections on a foreign machine (C1 decision). Mirrors
 * `DatabaseEngineId` (src/core/engines) — kept local so this shared type
 * module stays dependency-light for the electron main process.
 */
export type DashboardEngineId = 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite' | 'kusto';

export type TileSource =
  | { kind: 'sql'; sql: string }
  | { kind: 'template'; templateId: string; sql: string } // sql copied at add-time, stays editable
  | { kind: 'vqe'; sql: string; vqeState?: unknown }; // VQE emits SQL; state kept for re-edit

export type TileRefresh = { mode: 'manual' } | { mode: 'interval'; seconds: number };

export type TileAlertRule = {
  operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
  threshold: number;
  background: boolean; // opt-in: keep refreshing this tile while dashboard hidden (separate budget, S12)
  cooldownSeconds: number; // min gap between notifications after re-arm
};

export type TileThreshold = {
  operator: '>' | '>=' | '<' | '<=';
  value: number;
  severity: 'warning' | 'critical';
};

/** Cross-filter contract (S11) — per tile, per dashboard field. */
export type FilterBinding = {
  dashboardField: string; // logical field name shared across tiles (e.g. 'channel')
  targetExpression: string; // column/expression in THIS tile's query the filter applies to
  valueType: 'string' | 'number' | 'date' | 'boolean';
};

export type TileLayout = { x: number; y: number; w: number; h: number }; // 12-col grid units

type TileBase = {
  id: string;
  title: string;
  layout: TileLayout;
  pageId?: string; // RESERVED for v1.1 dashboard tabs/pages — serialized, unused in v1 UI
};
type QueryTileBase = TileBase & {
  connectionId: string;
  database?: string;
  /** Source engine — powers S14 import-remap "engine-compatible connections". Populated from S6 onward. */
  engine?: DashboardEngineId;
  connectionRole?: string; // RESERVED for v1.5 environment lenses — serialized, unused in v1 UI
  source: TileSource;
  refresh: TileRefresh;
  resultSetIndex?: number; // default 0
  filterBindings?: FilterBinding[];
  /**
   * Healthy-empty copy shown when a run returns 0 rows and that's GOOD news
   * (S7 templates seed it, e.g. "No sessions are currently blocked."). Absent
   * on hand-authored tiles — those fall back to the generic "No data".
   */
  emptyStateCopy?: string;
};

export type ChartTile = QueryTileBase & {
  tileType: 'chart';
  chartConfig: ChartConfig;
  /** Target lines / goal bands (Recharts ReferenceLine; rendered in S3, configured in S6). */
  referenceLines?: Array<{ value: number; label?: string; severity?: 'warning' | 'critical' }>;
};

/** Discriminated — invalid combinations unrepresentable. */
export type StatFormat =
  | { kind: 'number' }
  | { kind: 'percent'; scale: 'fraction' | 'percent' } // 0.42 vs 42
  | { kind: 'currency'; code: string }
  | { kind: 'duration'; unit: 'ms' | 's' | 'min' }; // source unit of the raw value
export type StatCompare =
  | { kind: 'none' }
  | { kind: 'previous-period'; column: string } // same row, prior-value column
  | { kind: 'target'; value: number };
export type StatTile = QueryTileBase & {
  tileType: 'stat';
  valueColumn?: string; // default: first numeric column
  format: StatFormat;
  compare: StatCompare;
  sparkline?: { timeColumn: string; valueColumn: string }; // explicit, never inferred
  thresholds?: TileThreshold[]; // conditional severity coloring / bands
  alertRule?: TileAlertRule; // alerts are stat-only in v1 (narrowed per review)
};
export type TableTile = QueryTileBase & {
  tileType: 'table';
  rowLimit: number;
  columns?: string[];
  density: 'compact' | 'comfortable';
  conditionalFormats?: Array<{ column: string; rule: TileThreshold }>;
};
export type PivotTile = QueryTileBase & {
  tileType: 'pivot';
  rowColumn: string;
  columnColumn: string;
  valueColumn: string;
  aggregation: AggregationFunction;
};
/**
 * Spatial/map tile — renders the query's geometry column(s) on the shared
 * spatial results map. Both columns are optional persisted preferences:
 * `MapTileView` passes them to the panel as `initialGeometryColumn` /
 * `initialColorBy`, seeding its pickers' initial state only (an in-panel
 * pick wins afterwards; a column missing from the current result falls back
 * to the panel's auto-pick / None).
 */
export type MapTile = QueryTileBase & {
  tileType: 'map';
  geometryColumn?: string;
  colorByColumn?: string;
};
export type NoteTile = TileBase & { tileType: 'note'; markdown: string };
export type DashboardTile = ChartTile | StatTile | TableTile | PivotTile | MapTile | NoteTile;

export type DashboardParameter =
  | { kind: 'time-range'; id: 'range'; defaultDays: 7 | 28 | 90; allowCustom: true } // custom from/to picker is v1 (B2)
  | {
      kind: 'static-list';
      id: string;
      label: string;
      values: string[];
      /** How values bind into driver parameters (S10) — default 'string'. */
      valueType?: ParamValueType;
      multi?: boolean;
      includeAll?: boolean;
      defaultValue?: string | string[]; // string[] iff multi
    }
  | {
      kind: 'query-list';
      id: string;
      label: string;
      connectionId: string;
      database?: string;
      engine?: DashboardEngineId; // source engine for S14 remap
      sql: string;
      labelColumn?: string;
      valueColumn?: string; // explicit column mapping
      /** How values bind into driver parameters (S10) — default 'string'. */
      valueType?: ParamValueType;
      multi?: boolean;
      includeAll?: boolean;
      defaultValue?: string | string[]; // string[] iff multi
      dependsOn?: string[]; // cascading: re-fetch when these params change
    }
  | { kind: 'free-text'; id: string; label: string; valueType?: ParamValueType; defaultValue?: string };

export type Dashboard = {
  id: string;
  name: string;
  /**
   * Optional free-text "what this dashboard is for" (S24 review). Additive —
   * definitions authored before it simply omit the field, so it needs no
   * version bump; it travels with export/import like any other definition
   * field (`serializeDefinition` spreads the whole dashboard).
   */
  description?: string;
  version: 1;
  revision: number; // optimistic-concurrency token; bumped by the repository on every mutation
  parameters: DashboardParameter[];
  tiles: DashboardTile[];
  playlist?: { dashboardIds: string[]; secondsPerDashboard: number }; // S13 — referential rules in repository API
  roleMap?: Record<string, string>; // RESERVED v1.5 environment lenses: role → connectionId
  createdAt: string;
  updatedAt: string;
};

/** Declared binding type for a parameter's values (typed seam for S10/S11 — C1). */
export type ParamValueType = 'string' | 'number' | 'boolean' | 'date';
export type ParamScalar = string | number | boolean;
/** Explicit persisted custom time range (UTC ISO strings). */
export type TimeRangeValue = { from: string; to: string };
export type BoundParamValue = ParamScalar | ParamScalar[] | TimeRangeValue;
export type BoundParams = Record<string, BoundParamValue>; // multi-select params bind as arrays

/** Persisted alert lifecycle — represents both re-arming AND cooldown history (S12 seam, C1). */
export type TileAlertState = {
  status: 'armed' | 'firing';
  lastFiredAt?: string;
  clearedAt?: string;
};

/** Sidecar — NEVER exported with definitions. Persisted at {userData}/dashboards/ui-state.json. */
export type DashboardUiState = {
  version: 1;
  paramValues?: BoundParams; // persisted selections (S10)
  alertState?: Record<string, TileAlertState>; // per-tile alert lifecycle (S12)
  dismissedSubtitles?: string[]; // starter-tile subtitles (S21)
  /**
   * Per-tile write-trust; invalidated when SQL changes (S3). SECURITY: granted
   * ONLY by the main process via a dedicated grant operation that hashes the
   * repository's own persisted tile SQL — the generic `setUiState` IPC strips
   * this field so no renderer can forge trust (C1 finding).
   */
  scheduleTrust?: Record<string, { queryHash: string }>;
};

/**
 * `setUiState` patch — record fields accept explicit `undefined` values as
 * per-key DELETE markers (C1: `mergeRecords` deletes on undefined; structured
 * clone preserves explicit-undefined properties across IPC).
 */
export type DashboardUiStatePatch = {
  paramValues?: Record<string, BoundParamValue | undefined>;
  alertState?: Record<string, TileAlertState | undefined>;
  dismissedSubtitles?: string[];
  /** Main-granted only; typed `never` so no caller can send it. The IPC
   * write path still strips it at runtime (untrusted renderer). */
  scheduleTrust?: never;
};

export const FREE_DASHBOARD_LIMIT = 2;
export const isQueryBackedTile = (t: DashboardTile): t is Exclude<DashboardTile, NoteTile> =>
  t.tileType !== 'note';

// ---- Repository result contract (main process is the ONLY writer) ----

export type RepoError = 'free-limit' | 'not-found' | 'invalid-definition' | 'conflict' | 'io';
/** `detail` on the ok arm carries non-fatal warnings (e.g. import dropped a dangling playlist id). */
export type RepoResult<T> =
  | { ok: true; value: T; detail?: string }
  | { ok: false; error: RepoError; detail?: string };

export type DashboardChange = {
  kind: 'created' | 'updated' | 'deleted';
  id: string;
  dashboard?: Dashboard;
};

export type DashboardListResult = {
  dashboards: Dashboard[];
  warnings: Array<{ file: string; error: string }>;
};
